import type {ReactElement, SyntheticEvent} from 'react'
import {useRef, useState} from 'react'
import {faCaretUp, faCaretDown} from '@fortawesome/free-solid-svg-icons'
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome'
import {isEqual} from 'lodash'
import size from 'lodash/size'
import {animateScroll} from 'react-scroll'
import {useSafeEffect, useSafeEffectExtraDeps} from '@freckle/react-hooks'
import {Button} from '@freckle/educator-materials/ts/components/button'
import {useSafeTranslation} from '@freckle/educator-materials/ts/helpers/translate'
import type {SortTypesValue} from '@freckle/classroom/ts/common/helpers/sorting'
import {Sorting} from '@freckle/classroom/ts/common/helpers/sorting'

type KeyedRow<Row, Detail> = {
  id: string
  row: Row
  details: Array<{id: string; detail: Detail}>
}

export type ExpandingTableData<Row, Detail> = Array<KeyedRow<Row, Detail>>

export type HeaderCell<RowKey> = {
  width?: string
  sortBy?: RowKey
  label: string | ReactElement
}

function renderHeaderCell<RowKey>(
  transform: (label: string | ReactElement, sortBy?: RowKey) => ReactElement,
): (cell: HeaderCell<RowKey>, index: number) => ReactElement {
  /* eslint-disable react/display-name */
  return function (cell: HeaderCell<RowKey>, index: number) {
    const maybeWidth = cell.width !== undefined ? {width: cell.width} : {}
    // It's fine to use the index here because these are never re-ordered and siblings won't
    // use an index-based key
    return (
      <th key={`header-cell-${index}`} style={maybeWidth} className="link-cursor">
        {transform(cell.label, cell.sortBy)}
      </th>
    )
  }
}

export type RowCell = {
  value: string | ReactElement
}

function renderRowCell(cell: RowCell, index: number) {
  // It's fine to use the index here because these are never re-ordered and siblings won't
  // use an index-based key
  return <td key={`row-cell-${index}`}>{cell.value}</td>
}

type ExpandingTableRowProps<Row, Detail> = {
  row: Row
  details: Array<{id: string; detail: Detail}>
  detailHeader: ReactElement
  renderRow: (row: Row) => Array<RowCell>
  addRowClassName?: (row: Row) => string
  renderDetailRow: (detail: Detail) => Array<RowCell>
  isInitiallyExpanded: boolean
}

function ExpandingTableRow<Row, Detail>(props: ExpandingTableRowProps<Row, Detail>) {
  const {
    isInitiallyExpanded,
    row,
    details,
    detailHeader,
    renderRow,
    addRowClassName,
    renderDetailRow,
  } = props
  const [expanded, setExpanded] = useState<boolean>(isInitiallyExpanded)
  const elementRef = useRef<HTMLElement | null>(null)

  useSafeEffect(() => {
    if (isInitiallyExpanded && elementRef.current !== null) {
      animateScroll.scrollTo(elementRef.current.offsetTop, {duration: 1000})
    }
  }, [isInitiallyExpanded])
  useSafeEffect(() => {
    setExpanded(isInitiallyExpanded)
  }, [isInitiallyExpanded])

  const icon = (
    <td
      onClick={() => setExpanded(prevState => !prevState)}
      className="expanding-table-icon text-center link-cursor"
    >
      <FontAwesomeIcon icon={expanded ? faCaretUp : faCaretDown} size="2x" />
    </td>
  )

  const rowCells = renderRow(row).map(renderRowCell)

  const className = addRowClassName ? addRowClassName(row) : ''

  const tableRow = (
    <tr className={`expanding-table-row ${className}`}>
      {rowCells}
      {icon}
    </tr>
  )

  const detailRows = details.map(detail => {
    return (
      <tr key={`detail-${detail.id}`} className="expanding-table-details-row">
        {renderDetailRow(detail.detail).map(renderRowCell)}
      </tr>
    )
  })

  const detailTable = expanded ? (
    <tr>
      <td colSpan={rowCells.length + 1}>
        <div className="expanding-table-details-container">
          <table className="expanding-table-details-table">
            <thead>{detailHeader}</thead>
            <tbody>{detailRows}</tbody>
          </table>
        </div>
      </td>
    </tr>
  ) : null

  return (
    <tbody
      ref={node => {
        elementRef.current = node
      }}
    >
      {tableRow}
      {detailTable}
    </tbody>
  )
}

type ExpandingTableProps<Row, Detail, RowKey, RowSortValue> = {
  getData: () => ExpandingTableData<Row, Detail> // Generate data for table
  filterBy?: (row: Row) => string // Return value from row to compare filter text against
  sortBy: RowKey // Initial key to project sort values from
  sortDir?: SortTypesValue // Direction to sort by
  sortValue: (key: RowKey, row: Row) => RowSortValue // Project sort values from Row
  renderHeader: () => Array<HeaderCell<RowKey>> // Generate array of header cells
  renderRow: (row: Row) => Array<RowCell> // Generate array of row cells from Row
  addRowClassName?: (row: Row) => string // Optionally add a class name to each row
  renderDetailHeader: () => Array<HeaderCell<void>> // Generate array of detail header cells
  renderDetailRow: (detail: Detail) => Array<RowCell> // Generate array of detail row cells from Detail
  underSearchBar?: ReactElement // Element to display under seach bar
  children?: ReactElement // Elements to display if no data
  studentIdOfRowToBeExpanded?: number | null | undefined // Row with matching student Id will be expanded when table is rendered
}

export function ExpandingTable<Row, Detail, RowSortValue>(
  props: ExpandingTableProps<Row, Detail, string, RowSortValue>,
) {
  const {t} = useSafeTranslation()
  const {
    getData,
    sortValue,
    renderHeader,
    renderDetailHeader,
    renderRow,
    renderDetailRow,
    addRowClassName,
    sortBy: propsSortBy,
    sortDir: propsSortDir,
    filterBy: propsFilterBy,
    underSearchBar: propsUnderSearchBar,
    children,
    studentIdOfRowToBeExpanded,
  } = props
  const dataValue = getData()
  const [data, setData] = useState<ExpandingTableData<Row, Detail>>(getData())
  const [sortBy, setSortBy] = useState<string>(propsSortBy)
  const [sortDir, setSortDir] = useState<SortTypesValue>(propsSortDir ?? Sorting.ASC)
  const [filterBy, setFilterBy] = useState<string | null | undefined>(null)
  const [filteredData, setFilteredData] = useState<ExpandingTableData<Row, Detail>>(
    Sorting.sortOn(data, sortDir, (x: KeyedRow<Row, Detail>) => sortValue(propsSortBy, x.row)),
  )

  useSafeEffectExtraDeps<{
    propsSortDir: SortTypesValue | undefined
    getData: () => ExpandingTableData<Row, Detail>
    dataValue: ExpandingTableData<Row, Detail>
    sortValue: (key: string, row: Row) => RowSortValue
    propsSortBy: string
  }>(
    ({propsSortBy, propsSortDir, getData, sortValue}) => {
      const data = getData()
      const sortDir = propsSortDir ?? Sorting.ASC
      const filteredData = Sorting.sortOn(data, sortDir, (x: KeyedRow<Row, Detail>) =>
        sortValue(propsSortBy, x.row),
      )
      setData(data)
      setSortBy(propsSortBy)
      setSortDir(sortDir)
      setFilterBy(null)
      setFilteredData(filteredData)
    },
    [],
    {
      getData: {value: getData, comparator: isEqual},
      dataValue: {value: dataValue, comparator: isEqual},
      propsSortBy: {value: propsSortBy, comparator: isEqual},
      propsSortDir: {value: propsSortDir, comparator: isEqual},
      sortValue: {value: sortValue, comparator: () => true},
    },
  )

  function onClickSortRows(sortByData: string) {
    const sortDirData = Sorting.changeSortDirIf(sortBy === sortByData, sortDir)
    updateRows(sortBy, sortDirData, filterBy ?? null)
  }

  function onChangeFilterRows(e: SyntheticEvent<EventTarget, Event>) {
    if (e.target instanceof HTMLInputElement) {
      const filterBy = e.target.value
      updateRows(sortBy, sortDir, filterBy)
    }
  }

  function updateRows(
    sortBy: string,
    sortDir: SortTypesValue,
    filterBy: string | null | undefined,
  ) {
    const filteredData = Sorting.sortOn(
      data.filter(x => {
        return filterBy !== null && filterBy !== undefined && propsFilterBy !== undefined
          ? propsFilterBy(x.row).toLowerCase().indexOf(filterBy.toLowerCase()) >= 0
          : true
      }),
      sortDir,
      (x: KeyedRow<Row, Detail>) => sortValue(sortBy, x.row),
    )
    setSortBy(sortBy)
    setSortDir(sortDir)
    setFilterBy(filterBy)
    setFilteredData(filteredData)
  }

  function renderSortableHeader(label: string | ReactElement, key?: string) {
    if (key !== undefined) {
      const sortDirArrow = Sorting.sortDirArrow(sortDir)

      return (
        <Button style="link" onClick={() => onClickSortRows(key)}>
          {label} {sortBy === key ? sortDirArrow : ''}
        </Button>
      )
    } else {
      return <span>{label}</span>
    }
  }
  const header = (
    <tr key="et-table-header" className="expanding-table-header">
      {renderHeader().map(renderHeaderCell(renderSortableHeader))}
      <th className="expanding-table-icon-width" />
    </tr>
  )

  const detailHeader = (key: string) => (
    <tr key={`${key}-detail-header`} className="expanding-table-details-header">
      {renderDetailHeader().map(renderHeaderCell((label, _sortBy) => <span>{label}</span>))}
    </tr>
  )

  const rows = filteredData.map(x => {
    const isInitiallyExpanded: boolean = parseInt(x.id, 10) === studentIdOfRowToBeExpanded

    return (
      <ExpandingTableRow
        key={`row-${x.id}`}
        row={x.row}
        details={x.details}
        detailHeader={detailHeader(x.id)}
        renderRow={renderRow}
        addRowClassName={addRowClassName}
        renderDetailRow={renderDetailRow}
        isInitiallyExpanded={isInitiallyExpanded}
      />
    )
  })

  const underSearchBar =
    size(data) !== 0 && propsUnderSearchBar !== undefined ? (
      <div className="col-12">{propsUnderSearchBar}</div>
    ) : null

  const filterData =
    propsFilterBy !== undefined ? (
      <div key="et-filter" className="col-12 std-margin-bottom-lg">
        <input
          onChange={onChangeFilterRows}
          className="form-control input-md"
          placeholder={t('EXPANDING_TABLE_SEARCH_INPUT_PLACEHOLDER')}
        />
      </div>
    ) : null

  return size(data) === 0 && children !== undefined ? (
    children
  ) : (
    <div className="row">
      {filterData}
      {underSearchBar}
      <div className="col-12">
        <table className="expanding-table">
          <thead>{header}</thead>
          {size(rows) === 0 ? <tbody /> : rows}
        </table>
      </div>
    </div>
  )
}
